import { useCallback, useEffect, useRef, useState } from 'react';
import type { DisponibilidadMes, Fecha, Hora } from '../../../compartido/tipos';
import { api } from './api';

/** true si la ventana cumple la media query. Se actualiza al rotar o redimensionar. */
export function useMedia(consulta: string): boolean {
  const [cumple, setCumple] = useState(() => typeof window !== 'undefined' && window.matchMedia(consulta).matches);
  useEffect(() => {
    const mq = window.matchMedia(consulta);
    const cambio = () => setCumple(mq.matches);
    cambio();
    mq.addEventListener('change', cambio);
    return () => mq.removeEventListener('change', cambio);
  }, [consulta]);
  return cumple;
}

/**
 * Disponibilidad de varios meses a la vez: el que se está mirando y el del
 * día elegido, que pueden no ser el mismo (elegir el 30 de setiembre y
 * asomarse a octubre no debe borrar las horas del 30). Cada mes se pide una
 * vez y queda en memoria mientras dura la visita; recargar los vuelve a
 * pedir (después de perder una hora contra otro cliente, por ejemplo).
 */
export function useDisponibilidad(...pedidos: Array<string | undefined>) {
  const meses = [...new Set(pedidos.filter((m): m is string => !!m))];
  const clave = meses.join(',');
  const [datos, setDatos] = useState<Record<string, DisponibilidadMes>>({});
  const [error, setError] = useState<unknown>(undefined);
  const [version, setVersion] = useState(0);
  const enVuelo = useRef(new Set<string>());

  useEffect(() => {
    let vivo = true;
    for (const mes of meses) {
      if (datos[mes] || enVuelo.current.has(mes)) continue;
      enVuelo.current.add(mes);
      setError(undefined);
      api.disponibilidad(mes).then(
        (d) => {
          enVuelo.current.delete(mes);
          if (vivo) setDatos((x) => ({ ...x, [mes]: d }));
        },
        (e) => {
          enVuelo.current.delete(mes);
          if (vivo) setError(e);
        },
      );
    }
    return () => {
      vivo = false;
    };
    // `meses` cambia de identidad en cada render; `clave` es su contenido.
  }, [clave, version, datos]);

  const recargar = useCallback(() => {
    enVuelo.current.clear();
    setDatos({});
    setVersion((v) => v + 1);
  }, []);

  const libres = useCallback(
    (fecha: Fecha): Hora[] => datos[fecha.slice(0, 7)]?.dias.find((d) => d.fecha === fecha)?.libres ?? [],
    [datos],
  );

  /** true si ese mes ya llegó. */
  const tiene = useCallback((mes: string | undefined) => !!mes && !!datos[mes], [datos]);

  return { tiene, error, libres, recargar };
}

/** Ejecuta `fn` cada `ms` mientras `activo`. Se detiene solo al desmontar. */
export function useCadaTanto(fn: () => void, ms: number, activo: boolean) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!activo) return;
    const id = window.setInterval(() => ref.current(), ms);
    return () => window.clearInterval(id);
  }, [ms, activo]);
}

/** El reloj, refrescado cada segundo. Para cuentas regresivas. */
export function useAhora(activo = true): Date {
  const [ahora, setAhora] = useState(() => new Date());
  useCadaTanto(() => setAhora(new Date()), 1000, activo);
  return ahora;
}

/** Guarda un valor en localStorage para la próxima visita (el último correo usado). */
export function recordarLocal(clave: string, valor: string): void {
  try {
    window.localStorage.setItem(clave, valor);
  } catch {
    /* sin almacenamiento, no pasa nada */
  }
}

export function leerLocal(clave: string): string {
  try {
    return window.localStorage.getItem(clave) ?? '';
  } catch {
    return '';
  }
}

/**
 * Una función con identidad fija que siempre ejecuta la versión más reciente
 * de `fn`. Para pasar callbacks a componentes que se reinician cuando cambia
 * la identidad de sus props (como el formulario de Mercado Pago).
 */
export function useEstable<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: A) => ref.current(...args), []);
}
